// Onboarding wizard — full-page, unskippable (except eBay step) walkthrough
// for first-time users. 4 steps, one question per page, back/forward navigation.
//
// Steps:
//   1. Price Targets        (auto_skip_below, sweet_spot_low, sweet_spot_high)
//   2. Always-Grab Brands   (defaults pre-checked, remove or add)
//   3. Always-Skip Brands   (defaults pre-checked, remove or add)
//   4. Connect eBay         (only skippable step — message shown if skipped)
//
// On finish: POST /api/auth/complete-onboarding flips the user flag so
// ProtectedRoute stops walling them. Then either navigate to
// /connect-ebay?ebay=connected&onboarding=1 (if they connected) or /scan
// with a toast message.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CheckCircle, Plus, Storefront, Tag, Trash, XCircle,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { authAPI, ebayAPI, prefsAPI } from "@/lib/api";
import Logo from "@/components/app/Logo";

const TOTAL_STEPS = 4;

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [step, setStep] = useState(1);
  const [prefs, setPrefs] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [skippedEbay, setSkippedEbay] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: d }] = await Promise.all([
          prefsAPI.get(),
          prefsAPI.defaults(),
        ]);
        setPrefs(p || {});
        setDefaults(d || {});
      } catch {
        setPrefs({});
        setDefaults({});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const persist = async (patch) => {
    setSaving(true);
    try {
      const { data } = await prefsAPI.update(patch);
      setPrefs(data);
    } catch {
      // best-effort — user can still continue
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    } else {
      // Final step submitted (handled by ConnectEbayStep itself)
    }
  };
  const back = () => setStep(Math.max(1, step - 1));

  const finishOnboarding = async ({ connectedEbay }) => {
    try {
      await authAPI.completeOnboarding();
      await refreshUser();
    } catch {
      // even on failure, navigate so the user isn't stuck
    }
    if (connectedEbay) {
      // eBay OAuth will itself land us at /connect-ebay?ebay=connected;
      // if it returns here (e.g., the user clicked the "i've connected"
      // confirmation), forward to that page so they can set defaults.
      navigate("/connect-ebay?ebay=connected&onboarding=1", { replace: true });
    } else {
      navigate("/scan?onboarded=1&skipped_ebay=1", { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Logo size={56} showText className="text-3xl justify-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col" data-testid="onboarding-page">
      {/* Top: progress dots */}
      <div className="bg-white border-b-4 border-black py-4 px-6 flex items-center justify-between">
        <Logo size={28} showText className="text-base" />
        <div className="flex items-center gap-1.5" data-testid="onboarding-progress">
          {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
            <div
              key={i}
              className={`w-2.5 h-2.5 border-2 border-black ${
                i + 1 < step ? "bg-[#00E676]" : i + 1 === step ? "bg-black" : "bg-white"
              }`}
            />
          ))}
        </div>
        <div className="font-heading text-xs font-bold uppercase tracking-wider text-neutral-500">
          {step}/{TOTAL_STEPS}
        </div>
      </div>

      {/* Step body */}
      <div className="flex-1 flex flex-col px-6 pt-8 pb-6 max-w-md mx-auto w-full">
        {step === 1 && (
          <PriceTargetsStep
            prefs={prefs}
            setPrefs={setPrefs}
            onNext={async () => {
              await persist({
                min_list_price: Number(prefs.min_list_price ?? 0),
                target_price_low: Number(prefs.target_price_low ?? 20),
                target_price_high: Number(prefs.target_price_high ?? 30),
              });
              setStep(2);
            }}
            saving={saving}
          />
        )}
        {step === 2 && (
          <BrandsStep
            kind="grab"
            title="Always GRAB brands"
            subtitle="When we see one of these brands, the verdict is auto-upgraded to GRAB. Remove any you don't trust, or add your own."
            color="#00E676"
            defaults={defaults?.always_grab_brands || []}
            value={prefs?.always_grab_brands || defaults?.always_grab_brands || []}
            onChange={(brands) => setPrefs({ ...prefs, always_grab_brands: brands })}
            onNext={async () => {
              await persist({ always_grab_brands: prefs.always_grab_brands || defaults?.always_grab_brands || [] });
              setStep(3);
            }}
            onBack={back}
            saving={saving}
          />
        )}
        {step === 3 && (
          <BrandsStep
            kind="skip"
            title="Always SKIP brands"
            subtitle="When we see one of these brands, the verdict is auto-downgraded to SKIP. Adjust as you learn what doesn't sell for you."
            color="#FF1744"
            defaults={defaults?.skip_brands || []}
            value={prefs?.skip_brands || defaults?.skip_brands || []}
            onChange={(brands) => setPrefs({ ...prefs, skip_brands: brands })}
            onNext={async () => {
              await persist({ skip_brands: prefs.skip_brands || defaults?.skip_brands || [] });
              setStep(4);
            }}
            onBack={back}
            saving={saving}
          />
        )}
        {step === 4 && (
          <ConnectEbayStep
            onBack={back}
            onSkip={async () => {
              setSkippedEbay(true);
              await finishOnboarding({ connectedEbay: false });
            }}
            onConnect={async () => {
              // Mark onboarding complete BEFORE redirecting to eBay so when
              // they come back to /connect-ebay they're not bounced back here.
              try {
                await authAPI.completeOnboarding();
                await refreshUser();
              } catch {}
              try {
                const { data } = await ebayAPI.connect();
                if (data?.auth_url) window.location.href = data.auth_url;
              } catch {
                // If we can't even get the auth URL, fall back to skip path
                await finishOnboarding({ connectedEbay: false });
              }
            }}
          />
        )}
      </div>
    </div>
  );
}

// ---- Step 1: price targets ----
function PriceTargetsStep({ prefs, setPrefs, onNext, saving }) {
  return (
    <div className="flex-1 flex flex-col" data-testid="onboarding-step-pricing">
      <div className="mb-6">
        <Tag size={36} weight="bold" className="mb-3" />
        <h1 className="font-heading text-3xl font-black uppercase tracking-tighter leading-none">
          What are your price targets?
        </h1>
        <p className="font-body text-sm text-neutral-600 mt-3">
          We use these to filter verdicts. You can change them any time in Profile.
        </p>
      </div>

      <div className="space-y-5 flex-1">
        <PriceInput
          label="Auto-Skip Below"
          help="If a verdict's projected sell price is below this, we'll auto-skip it. Set $0 to disable."
          value={prefs?.min_list_price ?? 0}
          onChange={(v) => setPrefs({ ...prefs, min_list_price: v })}
          testid="onboard-min-price"
        />
        <PriceInput
          label="Sweet Spot Low"
          help="Items priced at this or above start landing in your sweet spot."
          value={prefs?.target_price_low ?? 20}
          onChange={(v) => setPrefs({ ...prefs, target_price_low: v })}
          testid="onboard-target-low"
        />
        <PriceInput
          label="Sweet Spot High"
          help="The top of your sweet spot. Anything above is bonus territory."
          value={prefs?.target_price_high ?? 30}
          onChange={(v) => setPrefs({ ...prefs, target_price_high: v })}
          testid="onboard-target-high"
        />
      </div>

      <PrimaryButton onClick={onNext} disabled={saving} testid="onboard-step1-next">
        Continue <ArrowRight size={18} weight="bold" />
      </PrimaryButton>
    </div>
  );
}

function PriceInput({ label, help, value, onChange, testid }) {
  return (
    <div>
      <label className="block font-heading text-xs font-bold uppercase tracking-widest mb-1.5">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <span className="font-heading text-xl font-black">$</span>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          min="0"
          step="1"
          className="w-32 border-2 border-black bg-white px-3 py-3 font-heading text-xl font-bold focus:outline-none focus:ring-4 focus:ring-black/20"
          data-testid={testid}
        />
      </div>
      <p className="font-body text-xs text-neutral-500 mt-1.5">{help}</p>
    </div>
  );
}

// ---- Steps 2 & 3: brand lists ----
function BrandsStep({ kind, title, subtitle, color, defaults, value, onChange, onNext, onBack, saving }) {
  const [input, setInput] = useState("");
  const brands = useMemo(() => Array.isArray(value) ? value : [], [value]);

  const add = () => {
    const b = input.trim();
    if (!b) return;
    if (brands.some((x) => x.toLowerCase() === b.toLowerCase())) {
      setInput("");
      return;
    }
    onChange([...brands, b]);
    setInput("");
  };
  const remove = (b) => onChange(brands.filter((x) => x !== b));
  const restoreDefaults = () => onChange(defaults || []);

  return (
    <div className="flex-1 flex flex-col" data-testid={`onboarding-step-${kind}`}>
      <div className="mb-5">
        <div
          className="inline-block px-3 py-1 font-heading text-[11px] font-bold uppercase tracking-widest border-2 border-black mb-3"
          style={{ background: color }}
        >
          {kind === "grab" ? "Step 2 of 4 · Always Grab" : "Step 3 of 4 · Always Skip"}
        </div>
        <h1 className="font-heading text-2xl font-black uppercase tracking-tighter leading-none">
          {title}
        </h1>
        <p className="font-body text-sm text-neutral-600 mt-2.5">{subtitle}</p>
      </div>

      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Add a brand…"
          className="flex-1 border-2 border-black bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-4 focus:ring-black/20"
          data-testid={`onboard-${kind}-input`}
        />
        <button
          onClick={add}
          className="px-3 border-2 border-black bg-black text-white"
          data-testid={`onboard-${kind}-add`}
        >
          <Plus size={16} weight="bold" />
        </button>
      </div>

      <div
        className="flex-1 flex flex-wrap gap-1.5 content-start border-2 border-neutral-200 p-3 bg-white max-h-72 overflow-y-auto"
        data-testid={`onboard-${kind}-list`}
      >
        {brands.length === 0 && (
          <div className="font-body text-xs text-neutral-400 italic">No brands yet. Add one above or restore defaults.</div>
        )}
        {brands.map((b) => (
          <span
            key={b}
            className="inline-flex items-center gap-1.5 border-2 border-black bg-white px-2 py-1 font-body text-xs font-semibold"
          >
            {b}
            <button onClick={() => remove(b)} className="hover:text-red-600" data-testid={`onboard-${kind}-remove-${b}`}>
              <Trash size={11} weight="bold" />
            </button>
          </span>
        ))}
      </div>

      <button
        onClick={restoreDefaults}
        className="mt-3 font-body text-xs font-semibold underline text-neutral-600 self-start"
        data-testid={`onboard-${kind}-restore`}
      >
        Restore defaults
      </button>

      <div className="mt-5 grid grid-cols-2 gap-2">
        <SecondaryButton onClick={onBack} testid={`onboard-${kind}-back`}>
          <ArrowLeft size={16} weight="bold" /> Back
        </SecondaryButton>
        <PrimaryButton onClick={onNext} disabled={saving} testid={`onboard-${kind}-next`}>
          Continue <ArrowRight size={18} weight="bold" />
        </PrimaryButton>
      </div>
    </div>
  );
}

// ---- Step 4: connect eBay ----
function ConnectEbayStep({ onBack, onSkip, onConnect }) {
  return (
    <div className="flex-1 flex flex-col" data-testid="onboarding-step-ebay">
      <div className="mb-6">
        <Storefront size={36} weight="bold" className="mb-3" />
        <h1 className="font-heading text-3xl font-black uppercase tracking-tighter leading-none">
          Connect your eBay account
        </h1>
        <p className="font-body text-sm text-neutral-600 mt-3">
          One-tap listing, automatic SKU sync, and sold-comp pricing all need eBay connected.
          You can skip this and do it later from Profile.
        </p>
      </div>

      <ul className="flex-1 space-y-3">
        <Benefit text="Publish to eBay directly from the app" />
        <Benefit text="Auto-fill brand, size, condition, and aspects from your photos" />
        <Benefit text="Reuse your eBay shipping, payment, and return policies" />
      </ul>

      <div className="space-y-2">
        <button
          onClick={onConnect}
          className="w-full bg-[#002FA7] text-white py-4 font-heading font-black text-base uppercase tracking-widest border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[1px] active:translate-x-[1px] active:shadow-none transition-all flex items-center justify-center gap-2"
          data-testid="onboard-ebay-connect"
        >
          <Storefront size={18} weight="bold" /> Connect eBay
        </button>
        <button
          onClick={onSkip}
          className="w-full bg-white text-black py-3 font-heading font-bold text-sm uppercase tracking-widest border-2 border-black active:bg-neutral-100"
          data-testid="onboard-ebay-skip"
        >
          Skip for now
        </button>
        <button
          onClick={onBack}
          className="w-full font-body text-xs text-neutral-500 underline pt-1"
          data-testid="onboard-ebay-back"
        >
          ← Back
        </button>
      </div>
    </div>
  );
}

function Benefit({ text }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle size={20} weight="bold" className="text-[#00E676] shrink-0 mt-0.5" />
      <span className="font-body text-sm">{text}</span>
    </li>
  );
}

// ---- Generic UI ----
function PrimaryButton({ children, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="mt-4 w-full bg-[#00E676] text-black py-4 font-heading font-black text-base uppercase tracking-widest border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[1px] active:translate-x-[1px] active:shadow-none transition-all disabled:bg-neutral-300 disabled:shadow-none flex items-center justify-center gap-2"
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full bg-white text-black py-4 font-heading font-bold text-base uppercase tracking-widest border-2 border-black active:bg-neutral-100 flex items-center justify-center gap-2"
      data-testid={testid}
    >
      {children}
    </button>
  );
}
